import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Card } from '../components/ui/Card';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import { X } from 'lucide-react';
import { debounce } from '../utils/debounce';
import { escapeIlikeToken, isLikelyUuid, matchesAllTokens, normalizeSearchInput, tokenize } from '../utils/tableSearch';

export default function AppointmentsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selectedAppointment, setSelectedAppointment] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [facilityFilter, setFacilityFilter] = useState<number | 'all'>('all');
  const [syncingFrom4D, setSyncingFrom4D] = useState(false);
  const [syncingToZoneDM, setSyncingToZoneDM] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [hasQuoteOnly, setHasQuoteOnly] = useState(false);
  const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);
  const [skipSynced, setSkipSynced] = useState(false);
  const [confirmZoneDMOpen, setConfirmZoneDMOpen] = useState(false);
  const [skipSyncedZoneDM, setSkipSyncedZoneDM] = useState(true); // Default: skip already synced
  const [premarkUnmapped, setPremarkUnmapped] = useState(false); // Onboarding: bulk-mark unmapped/skippable first
  const [dateFilter, setDateFilter] = useState<'3months' | '6months' | '12months' | 'all'>('3months'); // Default: Last 3 months
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest'); // Default: newest first
  const [syncStatusFilter, setSyncStatusFilter] = useState<string>('all'); // Default: show all statuses
  const topScrollbarRef = useRef<HTMLDivElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [topScrollbarWidth, setTopScrollbarWidth] = useState(2000);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(normalizeSearchInput(value));
        setPage(1);
      }, 250),
    []
  );

  useEffect(() => {
    debouncedSetSearchTerm(searchInput);
  }, [searchInput, debouncedSetSearchTerm]);

  useEffect(() => {
    const syncScrollbarMetrics = () => {
      if (!tableContainerRef.current) return;
      setTopScrollbarWidth(tableContainerRef.current.scrollWidth);
      if (topScrollbarRef.current) {
        topScrollbarRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
      }
    };

    syncScrollbarMetrics();
    window.addEventListener('resize', syncScrollbarMetrics);
    return () => window.removeEventListener('resize', syncScrollbarMetrics);
  }, [rowsPerPage, facilityFilter, hasQuoteOnly, searchTerm, dateFilter, sortOrder, syncStatusFilter, page]);

  // Facilities for filtering
  const { data: facilities } = useQuery({
    queryKey: ['facility_locations'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('facility_locations')
        .select('*')
        .eq('user_id', session.user.id)
        .order('facility_location_name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch appointments from Supabase with server-side pagination and filtering
  const { data: appointments, isLoading } = useQuery({
    queryKey: ['appointments-table', page, rowsPerPage, facilityFilter, searchTerm, hasQuoteOnly, dateFilter, sortOrder, syncStatusFilter],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      // Calculate pagination range
      const from = (page - 1) * rowsPerPage;
      const to = from + rowsPerPage - 1;
      
      // Build query with filters - include relationships that have FK constraints
      let query = supabase
        .from('appointments')
        .select(`
          *,
          appointment_types(name),
          appointment_subtypes(name),
          patients(first_name, last_name, id),
          staff(first_name, last_name),
          providers(first_name, last_name, degree)
        `, { count: 'exact' })
        .eq('user_id', session.user.id);
      
      // Facility filter
      if (facilityFilter !== 'all') {
        query = query.eq('facility_location_id', facilityFilter as number);
      }
      
      // Date filter: 
      // - For "received" status (and other sync statuses), filter by external_updated_at (when received/updated from 4D)
      // - For "all" status or synced appointments, filter by start_time (appointment date)
      // This ensures we see recently received appointments even if they have old start_time values
      if (dateFilter !== 'all') {
        const cutoffDate = new Date();
        if (dateFilter === '3months') {
          cutoffDate.setMonth(cutoffDate.getMonth() - 3);
        } else if (dateFilter === '6months') {
          cutoffDate.setMonth(cutoffDate.getMonth() - 6);
        } else if (dateFilter === '12months') {
          cutoffDate.setMonth(cutoffDate.getMonth() - 12);
        }
        
        // Use external_updated_at for sync status filters (received, zonedm_error, waiting_for_patient, empty)
        // Use start_time for "all" status or "zonedm_synced" status
        if (syncStatusFilter !== 'all' && syncStatusFilter !== 'zonedm_synced' && syncStatusFilter !== 'synced') {
          // Filter by external_updated_at for non-synced statuses
          query = query.gte('external_updated_at', cutoffDate.toISOString());
        } else {
          // Filter by start_time for all statuses or synced statuses
          query = query.gte('start_time', cutoffDate.toISOString());
        }
      }
      
      // Sync status filter: filter by zonedm_sync_status
      if (syncStatusFilter !== 'all') {
        if (syncStatusFilter === 'empty') {
          // Filter for null or empty string values - use PostgREST format
          query = query.or('zonedm_sync_status.is.null,zonedm_sync_status.eq.');
        } else {
          query = query.eq('zonedm_sync_status', syncStatusFilter);
        }
      }
      
      const normalizedSearch = normalizeSearchInput(searchTerm);
      const searchTokens = tokenize(normalizedSearch);

      // Search filter: tokenized lookup across text + IDs
      if (searchTokens.length > 0) {
        const escapedTokens = searchTokens.map(escapeIlikeToken).filter(Boolean);

        // First, find matching patients so patient-name tokens can resolve to appointment.patient_id
        let matchingPatientIds: string[] = [];
        try {
          const patientSearchConditions: string[] = [];
          escapedTokens.forEach((escapedToken, index) => {
            const rawToken = searchTokens[index];
            patientSearchConditions.push(`first_name.ilike.%${escapedToken}%`);
            patientSearchConditions.push(`last_name.ilike.%${escapedToken}%`);
            patientSearchConditions.push(`email.ilike.%${escapedToken}%`);

            if (/^\d+$/.test(rawToken)) {
              patientSearchConditions.push(`external_id.eq.${rawToken}`);
              patientSearchConditions.push(`account_number.ilike.%${escapeIlikeToken(rawToken)}%`);
            }

            if (isLikelyUuid(rawToken)) {
              patientSearchConditions.push(`id.eq.${rawToken}`);
            }
          });

          if (patientSearchConditions.length > 0) {
            const { data: matchingPatients, error: patientSearchError } = await supabase
              .from('patients')
              .select('id, first_name, last_name, external_id, account_number, email')
              .eq('user_id', session.user.id)
              .or(patientSearchConditions.join(','));

            if (patientSearchError) {
              console.error('Patient search error:', patientSearchError);
            } else if (matchingPatients && matchingPatients.length > 0) {
              matchingPatientIds = matchingPatients
                .filter((p: any) => matchesAllTokens([
                  p.id,
                  p.first_name,
                  p.last_name,
                  p.external_id,
                  p.account_number,
                  p.email,
                ], searchTokens))
                .map((p: any) => p.id)
                .filter(Boolean);
            }
          }
        } catch (patientSearchError) {
          console.warn('Patient name search failed:', patientSearchError);
        }

        // Build base query WITHOUT .or() calls - we'll add those separately
        const buildBaseQuery = () => {
          let baseQuery = supabase
            .from('appointments')
            .select(`
              *,
              appointment_types(name),
              appointment_subtypes(name),
              patients(first_name, last_name, id, external_id, account_number),
              staff(first_name, last_name),
              providers(first_name, last_name, degree)
            `, { count: 'exact' })
            .eq('user_id', session.user.id);

          // Apply facility filter
          if (facilityFilter !== 'all') {
            baseQuery = baseQuery.eq('facility_location_id', facilityFilter as number);
          }

          // Apply date filter
          // - For "received" status (and other sync statuses), filter by external_updated_at (when received/updated from 4D)
          // - For "all" status or synced appointments, filter by start_time (appointment date)
          if (dateFilter !== 'all') {
            const cutoffDate = new Date();
            if (dateFilter === '3months') {
              cutoffDate.setMonth(cutoffDate.getMonth() - 3);
            } else if (dateFilter === '6months') {
              cutoffDate.setMonth(cutoffDate.getMonth() - 6);
            } else if (dateFilter === '12months') {
              cutoffDate.setMonth(cutoffDate.getMonth() - 12);
            }

            // Use external_updated_at for sync status filters (received, zonedm_error, waiting_for_patient, empty)
            // Use start_time for "all" status or "zonedm_synced" status
            if (syncStatusFilter !== 'all' && syncStatusFilter !== 'zonedm_synced' && syncStatusFilter !== 'synced') {
              // Filter by external_updated_at for non-synced statuses
              baseQuery = baseQuery.gte('external_updated_at', cutoffDate.toISOString());
            } else {
              // Filter by start_time for all statuses or synced statuses
              baseQuery = baseQuery.gte('start_time', cutoffDate.toISOString());
            }
          }

          // Apply sync status filter
          if (syncStatusFilter !== 'all') {
            if (syncStatusFilter === 'empty') {
              // Filter for null or empty string values - use PostgREST format
              baseQuery = baseQuery.or('zonedm_sync_status.is.null,zonedm_sync_status.eq.');
            } else {
              baseQuery = baseQuery.eq('zonedm_sync_status', syncStatusFilter);
            }
          }

          return baseQuery;
        };

        const textSearchConditions: string[] = [];
        escapedTokens.forEach((escapedToken, index) => {
          const rawToken = searchTokens[index];

          textSearchConditions.push(`subject.ilike.%${escapedToken}%`);
          textSearchConditions.push(`zonedm_sync_status.ilike.%${escapedToken}%`);
          textSearchConditions.push(`cancel_reason.ilike.%${escapedToken}%`);
          textSearchConditions.push(`consult_reason.ilike.%${escapedToken}%`);
          textSearchConditions.push(`zonedm_contact_id.ilike.%${escapedToken}%`);
          textSearchConditions.push(`zonedm_event_id.ilike.%${escapedToken}%`);
          textSearchConditions.push(`zonedm_opportunity_id.ilike.%${escapedToken}%`);

          if (/^\d+$/.test(rawToken)) {
            textSearchConditions.push(`external_id.eq.${rawToken}`);
            textSearchConditions.push(`quote_number.eq.${rawToken}`);
            textSearchConditions.push(`quote_external_id.eq.${rawToken}`);
            textSearchConditions.push(`facility_location_id.eq.${rawToken}`);
          }

          if (isLikelyUuid(rawToken)) {
            textSearchConditions.push(`id.eq.${rawToken}`);
            textSearchConditions.push(`patient_id.eq.${rawToken}`);
            textSearchConditions.push(`provider_id.eq.${rawToken}`);
            textSearchConditions.push(`staff_id.eq.${rawToken}`);
            textSearchConditions.push(`type_id.eq.${rawToken}`);
            textSearchConditions.push(`subtype_id.eq.${rawToken}`);
          }
        });

        let textResults: any[] = [];
        if (textSearchConditions.length > 0) {
          const textQuery = buildBaseQuery().or(textSearchConditions.join(','));
          const { data: rawTextResults, error: textError } = await textQuery.order('external_updated_at', { ascending: sortOrder === 'oldest' });
          if (textError) {
            console.error('Text search query error:', textError);
            throw textError;
          }
          textResults = rawTextResults || [];
        }

        // Query 2: Patient ID matches (if we have matching patients)
        let patientResults: any[] = [];
        if (matchingPatientIds.length > 0) {
          const BATCH_SIZE = 100;
          const batches: string[][] = [];

          for (let i = 0; i < matchingPatientIds.length; i += BATCH_SIZE) {
            batches.push(matchingPatientIds.slice(i, i + BATCH_SIZE));
          }

          for (const batch of batches) {
            const patientQuery = buildBaseQuery().in('patient_id', batch);
            const { data: patientData, error: patientError } = await patientQuery.order('external_updated_at', { ascending: sortOrder === 'oldest' });

            if (patientError) {
              console.error('Patient ID query error (batch):', patientError);
              continue;
            }

            if (patientData) {
              patientResults.push(...patientData);
            }
          }
        }

        const combinedResults = new Map<string, any>();
        textResults.forEach((appt: any) => {
          combinedResults.set(appt.id, appt);
        });
        patientResults.forEach((appt: any) => {
          combinedResults.set(appt.id, appt);
        });

        let allResults = Array.from(combinedResults.values()).sort((a: any, b: any) => {
          const timeA = a.external_updated_at ? new Date(a.external_updated_at).getTime() : 0;
          const timeB = b.external_updated_at ? new Date(b.external_updated_at).getTime() : 0;
          return sortOrder === 'newest' ? timeB - timeA : timeA - timeB;
        });

        if (hasQuoteOnly) {
          allResults = allResults.filter((appt: any) =>
            appt.quote_number !== null || appt.quote_external_id !== null
          );
        }

        // Final pass enforces AND semantics across all tokens.
        allResults = allResults.filter((appt: any) =>
          matchesAllTokens([
            appt.id,
            appt.external_id,
            appt.patient_id,
            appt.quote_number,
            appt.quote_external_id,
            appt.facility_location_id,
            appt.subject,
            appt.consult_reason,
            appt.cancel_reason,
            appt.zonedm_sync_status,
            appt.zonedm_contact_id,
            appt.zonedm_event_id,
            appt.zonedm_opportunity_id,
            appt?.patients?.id,
            appt?.patients?.external_id,
            appt?.patients?.account_number,
            appt?.patients?.first_name,
            appt?.patients?.last_name,
            appt?.appointment_types?.name,
            appt?.appointment_subtypes?.name,
            appt?.providers?.first_name,
            appt?.providers?.last_name,
            appt?.staff?.first_name,
            appt?.staff?.last_name,
          ], searchTokens)
        );

        const paginatedResults = allResults.slice(from, to + 1);

        return {
          data: paginatedResults,
          count: allResults.length,
        };
      }
      
      // No search term - apply quote filter if needed
      if (hasQuoteOnly) {
        query = query.or('quote_number.not.is.null,quote_external_id.not.is.null');
      }
      
      // Apply pagination and ordering
      // For "received" status and other sync statuses, always sort by external_updated_at (when received from 4D)
      // For "all" or "synced" status, sort by external_updated_at (still most relevant for sync tracking)
      // This ensures "most recent" means "most recently received/updated from 4D", not "most recent appointment date"
      const { data, error, count } = await query
        .order('external_updated_at', { ascending: sortOrder === 'oldest' })
        .range(from, to);
      
      if (error) throw error;
      return { data: data || [], count: count || 0 };
    },
  });

  // Extract data and count from query result
  const appointmentsData = appointments?.data || [];
  const appointmentsCount = appointments?.count || 0;
  const totalPages = Math.max(1, Math.ceil(appointmentsCount / rowsPerPage));

  useEffect(() => {
    if (!tableContainerRef.current) return;
    setTopScrollbarWidth(tableContainerRef.current.scrollWidth);
    if (topScrollbarRef.current) {
      topScrollbarRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
  }, [appointmentsData, rowsPerPage]);

  // Fetch procedures for appointments with quotes - query via quote_external_id
  const quoteExternalIds = [...new Set(
    appointmentsData
      .filter((a: any) => a.quote_external_id)
      .map((a: any) => a.quote_external_id)
  )];
  
  const { data: proceduresData } = useQuery({
    queryKey: ['appointment-procedures', quoteExternalIds.join(',')],
    queryFn: async () => {
      if (quoteExternalIds.length === 0) return {};
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      // Fetch quotes with their procedure_description and appointment_uuid
      const { data: quotes, error: quotesError } = await supabase
        .from('quotes')
        .select('price_quote_id, appointment_uuid, procedure_description')
        .eq('user_id', session.user.id)
        .in('price_quote_id', quoteExternalIds);
      
      if (quotesError) throw quotesError;
      
      const quoteIds = (quotes || []).map((q: any) => q.price_quote_id);
      if (quoteIds.length === 0) return {};
      
      // Try to fetch procedures via quote_procedure_mapping first
      const { data: procedures, error: procError } = await supabase
        .from('quote_procedure_mapping')
        .select(`
          quote_id,
          procedures:procedures!inner(name)
        `)
        .eq('user_id', session.user.id)
        .in('quote_id', quoteIds);
      
      // Non-fatal if this fails - we'll use procedure_description instead
      
      // Map appointment_uuid -> procedure names
      const procedureMap: Record<string, string[]> = {};
      (quotes || []).forEach((q: any) => {
        if (!q.appointment_uuid) return;
        
        // First, try to get procedures from quote_procedure_mapping
        const mappedProcs = (procedures || [])
          .filter((p: any) => p.quote_id === q.price_quote_id)
          .map((p: any) => p.procedures?.name)
          .filter(Boolean);
        
        if (mappedProcs.length > 0) {
          // Use mapped procedures if available
          procedureMap[q.appointment_uuid] = mappedProcs;
        } else if (q.procedure_description) {
          // Fallback to procedure_description from quote
          procedureMap[q.appointment_uuid] = [q.procedure_description];
        }
      });
      
      return procedureMap;
    },
    enabled: quoteExternalIds.length > 0,
  });
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get('search');
    if (s) {
      const normalized = normalizeSearchInput(s);
      setSearchInput(normalized);
      setSearchTerm(normalized);
    }
  }, []);

  // Helper to render facility name from map (must be defined before use in filtering)
  const facilityNameByExternalId = (extId?: number | null) => {
    if (!extId) return '—';
    const match = (facilities || []).find((f: any) => f.external_id === extId);
    return match?.facility_location_name || String(extId);
  };

  // Total count query (for display purposes)
  const { data: appointmentsTotalCount = 0, error: totalCountError } = useQuery({
    queryKey: ['appointments-total-count'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { count, error } = await supabase
        .from('appointments')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', session.user.id);
      if (error) {
        console.error('[AppointmentsPage] Error fetching total appointment count:', error);
        throw error;
      }
      console.log('[AppointmentsPage] Total appointment count:', count, 'for user:', session.user.id);
      return count || 0;
    },
  });

  // Synced count (zonedm_synced status)
  const { data: appointmentsSyncedCount = 0, error: syncedCountError } = useQuery({
    queryKey: ['appointments-synced-count'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { count, error } = await supabase
        .from('appointments')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .eq('zonedm_sync_status', 'zonedm_synced');
      if (error) {
        console.error('[AppointmentsPage] Error fetching synced appointment count:', error);
        throw error;
      }
      console.log('[AppointmentsPage] Synced appointment count:', count, 'for user:', session.user.id);
      return count || 0;
    },
  });
  
  // Log errors if any
  useEffect(() => {
    if (totalCountError) {
      console.error('[AppointmentsPage] Total count query error:', totalCountError);
    }
    if (syncedCountError) {
      console.error('[AppointmentsPage] Synced count query error:', syncedCountError);
    }
  }, [totalCountError, syncedCountError]);

  // Paginated appointments are already server-side paginated
  const paginatedAppointments = appointmentsData;

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      // Scroll to top of table when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  const syncFrom4D = async (skipSyncedOption: boolean = false) => {
    setSyncingFrom4D(true);
    const progressToastId = 'appointments-progress';
    try {
      // Calculate date range based on selected date filter
      // For "all", don't set dates - will fetch everything from 4D
      let fromDate: Date | undefined;
      let toDate: Date | undefined;
      if (dateFilter === '3months') {
        fromDate = new Date();
        fromDate.setMonth(fromDate.getMonth() - 3);
        toDate = new Date(); // Default to now
      } else if (dateFilter === '6months') {
        fromDate = new Date();
        fromDate.setMonth(fromDate.getMonth() - 6);
        toDate = new Date(); // Default to now
      } else if (dateFilter === '12months') {
        fromDate = new Date();
        fromDate.setMonth(fromDate.getMonth() - 12);
        toDate = new Date(); // Default to now
      }
      // For 'all', fromDate and toDate remain undefined - will fetch all appointments
      
      // Calculate initial skip if skipSynced is enabled
      let initialSkip: number | undefined = undefined;
      if (skipSyncedOption) {
        // Get total from Supabase (already synced appointments)
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Not authenticated');
        const { count } = await supabase
          .from('appointments')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', session.user.id);
        const existingCount = count || 0;
        
        // Calculate which page to start from: floor(existingCount / 100) * 100
        // This rounds down to the nearest page boundary
        const skipPages = Math.floor(existingCount / 100);
        initialSkip = skipPages * 100;
        
        // Make one call to 4D to get the total and verify
        const { data: { session: session2 } } = await supabase.auth.getSession();
        const accessTokenCheck = session2?.access_token;
        if (accessTokenCheck) {
          // Get 4D integration config to make direct call
          const { data: integration } = await supabase
            .from('integrations')
            .select('config')
            .eq('type', '4d')
            .eq('user_id', session.user.id)
            .single();
          
          if (integration?.config) {
            const cfg = integration.config as any;
            const headers = {
              'client-id': cfg.clientId || cfg.client_id,
              'client-secret': cfg.clientSecret || cfg.client_secret,
              'subscription-key': cfg.subscriptionKey || cfg.subscription_key,
              'Accept': 'application/json',
            };
            // Get total count from 4D using the same date filter as the sync
            // Use fromModifiedDate/toModifiedDate to filter by when 4D last modified the appointment
            // If dateFilter is 'all', don't pass date parameters (fetch everything)
            let totalUrl = 'https://api.4d-emr.com/api/public/appointments?options.includeOriginalStart=true&options.skip=0&options.count=1&options.needTotalCount=true';
            if (fromDate && toDate) {
              const fromModifiedDateParam = encodeURIComponent(fromDate.toISOString());
              const toModifiedDateParam = encodeURIComponent(toDate.toISOString());
              totalUrl = `https://api.4d-emr.com/api/public/appointments?options.fromModifiedDate=${fromModifiedDateParam}&options.toModifiedDate=${toModifiedDateParam}&options.includeOriginalStart=true&options.skip=0&options.count=1&options.needTotalCount=true`;
            }
            const totalRes = await fetch(totalUrl, { headers: headers as any });
            if (totalRes.ok) {
              const totalData = await totalRes.json().catch(() => ({}));
              const totalFrom4D = totalData?.TotalCount;
              if (typeof totalFrom4D === 'number') {
                toast.loading(`Found ${totalFrom4D} total in 4D, ${existingCount} already synced. Starting from page ${skipPages + 1} (skip ${initialSkip})`, { id: progressToastId });
              }
            }
          }
        }
      }

      // Do NOT cache token for long loops; refetch fresh token each request
      // Call repeatedly until isLastPage = true (fetch -> process per page)
      let isLastPage = false;
      let logId: string | undefined;
      let totalEstimate: number | undefined;
      let isFirstCall = true;
      
      while (!isLastPage) {
        // Fetch a page and stage it (retry for transient 4D/edge errors, including 504 timeouts)
        let fetchJson: any;
        {
          const maxAttempts = 5; // Increased for 504 retries
          let attempt = 0;
          while (true) {
            attempt++;
            const latest = await supabase.auth.getSession();
            const accessToken = latest.data.session?.access_token;
            const requestBody: any = { logId };
            // On first call, if skipSynced is enabled, pass forceSkip
            if (isFirstCall && initialSkip !== undefined) {
              requestBody.forceSkip = initialSkip;
            }
            // Pass date filters to honor UI date filter settings (only if not 'all')
            // Use fromModifiedDate/toModifiedDate to filter by when 4D last modified the appointment
            // This matches what we store in external_updated_at and is what 4D uses for ModifiedDate
            if (fromDate && toDate) {
              requestBody.fromModifiedDate = fromDate.toISOString();
              requestBody.toModifiedDate = toDate.toISOString();
            }
            // If dateFilter is 'all', don't pass date parameters - will fetch everything
            let res: Response;
            try {
              res = await fetch(`${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/appointments-fetch`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify(requestBody),
              });
            } catch (fetchErr: any) {
              // Network errors - retry with backoff
              if (attempt < maxAttempts) {
                const backoffMs = Math.min(2000 * Math.pow(2, attempt - 1), 10000);
                toast.loading(`Network error, retrying fetch... (${attempt}/${maxAttempts})`, { id: progressToastId });
                await new Promise(r => setTimeout(r, backoffMs));
                continue;
              }
              throw new Error(`Fetch failed after ${maxAttempts} attempts: ${fetchErr?.message || 'Unknown error'}`);
            }
            const json = await res.json().catch(() => ({}));
            if (res.ok) { fetchJson = json; break; }
            // Handle 504 Gateway Timeout specifically with longer backoff
            if (res.status === 504 && attempt < maxAttempts) {
              const backoffMs = Math.min(3000 * Math.pow(2, attempt - 1), 15000);
              toast.loading(`Gateway timeout, retrying fetch... (${attempt}/${maxAttempts})`, { id: progressToastId });
              await new Promise(r => setTimeout(r, backoffMs));
              continue;
            }
            if (attempt >= maxAttempts) throw new Error(json.error || `Fetch failed: ${res.status} ${res.statusText}`);
            await new Promise(r => setTimeout(r, 1500 * attempt));
          }
        }
        isFirstCall = false;

        logId = fetchJson.log_id;
        const batchId = fetchJson.batchId as string;
        isLastPage = !!fetchJson.isLastPage;
        totalEstimate = fetchJson.totalEstimate;
        const skip = fetchJson.skip || 0;
        const fetchPageSize = Math.max(1, Number(fetchJson.pageSize || fetchJson.itemsCount || 100));
        const est = totalEstimate ? `/${totalEstimate}` : '';

        toast.loading(
          `Fetched ${fetchJson.itemsCount}. Processing ${skip}${est} (page ${Math.floor(skip / fetchPageSize)})`,
          { id: progressToastId }
        );

        // Process that staged batch (retry mild transient errors, including 504 timeouts)
        {
          const maxAttempts = 5; // Increased for 504 retries
          let attempt = 0;
          while (true) {
            attempt++;
            const latest2 = await supabase.auth.getSession();
            const accessToken2 = latest2.data.session?.access_token;
            let res: Response;
            try {
              res = await fetch(`${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/appointments-process`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${accessToken2}`,
                },
                body: JSON.stringify({ logId, batchId }),
              });
            } catch (fetchErr: any) {
              // Network errors - retry with backoff
              if (attempt < maxAttempts) {
                const backoffMs = Math.min(2000 * Math.pow(2, attempt - 1), 10000);
                toast.loading(`Network error, retrying process... (${attempt}/${maxAttempts})`, { id: progressToastId });
                await new Promise(r => setTimeout(r, backoffMs));
                continue;
              }
              throw new Error(`Process failed after ${maxAttempts} attempts: ${fetchErr?.message || 'Unknown error'}`);
            }
            const json = await res.json().catch(() => ({}));
            if (res.ok) break;
            // Handle 504 Gateway Timeout specifically with longer backoff
            if (res.status === 504 && attempt < maxAttempts) {
              const backoffMs = Math.min(3000 * Math.pow(2, attempt - 1), 15000);
              toast.loading(`Gateway timeout, retrying process... (${attempt}/${maxAttempts})`, { id: progressToastId });
              await new Promise(r => setTimeout(r, backoffMs));
              continue;
            }
            if (attempt >= maxAttempts) throw new Error(json.error || `Process failed: ${res.status} ${res.statusText}`);
            await new Promise(r => setTimeout(r, 1000 * attempt));
          }
        }

        // Refresh table data
        queryClient.invalidateQueries({ queryKey: ['appointments-total-count'] });
        queryClient.invalidateQueries({ queryKey: ['appointments-synced-count'] });
        queryClient.invalidateQueries({ queryKey: ['appointments-table'] });
      }

      toast.success(`Sync complete!`, { id: progressToastId });
    } catch (err: any) {
      toast.error('Sync FROM 4D failed: ' + (err.message || err.error_description || 'Unknown error'));
    } finally {
      setSyncingFrom4D(false);
    }
  };

  // Sync TO ZoneDM: ensure patients are pushed first, then push appointments
  const syncToZoneDM = async (skipSyncedOption: boolean = true, premarkUnmappedOption: boolean = false) => {
    setSyncingToZoneDM(true);
    const toastId = toast.loading('Syncing TO ZoneDM...');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const userId = session.user.id;

      // 0) Optional onboarding speedup: bulk-mark unmapped / skip_opportunity types before pushing
      if (premarkUnmappedOption) {
        toast.loading('Premarking unmapped/skippable appointments...', { id: toastId });
        try {
          const premarkUrl = `/.netlify/functions/premark-unmapped-appointments?user_id=${encodeURIComponent(userId)}`;
          const premarkRes = await fetch(premarkUrl, {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          const premarkBody = await premarkRes.json().catch(() => ({}));
          if (!premarkRes.ok) {
            console.warn('Premark unmapped failed:', premarkBody?.error);
            toast('Premark failed — continuing sync', { id: toastId, icon: '⚠️' });
          } else {
            const marked = Number(premarkBody?.total || 0);
            console.log('Premark unmapped succeeded:', premarkBody);
            toast.loading(
              marked > 0
                ? `Premarked ${marked} skippable appointments. Syncing TO ZoneDM...`
                : 'Syncing TO ZoneDM...',
              { id: toastId }
            );
            queryClient.invalidateQueries({ queryKey: ['appointments-table'] });
            queryClient.invalidateQueries({ queryKey: ['appointments-synced-count'] });
          }
        } catch (premarkErr: any) {
          console.warn('Premark unmapped error (continuing):', premarkErr);
          toast('Premark timed out — continuing sync', { id: toastId, icon: '⚠️' });
        }
      }

      // 1) Push patients first to ensure contact exists in ZoneDM (non-blocking - continue if it fails)
      const pushPatientsUrl = `/.netlify/functions/push-patients?user_id=${encodeURIComponent(userId)}&skip_synced=${skipSyncedOption ? 'true' : 'false'}`;
      try {
        const res1 = await fetch(pushPatientsUrl, {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const body1 = await res1.json().catch(() => ({}));
        if (!res1.ok) {
          console.warn('Push patients failed but continuing with appointments:', body1?.error);
          toast('Patient sync skipped (continuing with appointments)', { id: toastId, icon: '⚠️' });
        } else {
          console.log('Push patients succeeded:', body1);
        }
      } catch (patientErr: any) {
        console.warn('Push patients error (continuing with appointments):', patientErr);
        toast('Patient sync timed out (continuing with appointments)', { id: toastId, icon: '⚠️' });
      }

      // 2) Push appointments (creates calendar events with contact links) in batches
      // Calculate date range based on selected date filter
      const isAllTime = dateFilter !== '3months' && dateFilter !== '6months' && dateFilter !== '12months';
      let sinceDate: Date | null = null;
      
      if (dateFilter === '3months') {
        sinceDate = new Date();
        sinceDate.setMonth(sinceDate.getMonth() - 3);
      } else if (dateFilter === '6months') {
        sinceDate = new Date();
        sinceDate.setMonth(sinceDate.getMonth() - 6);
      } else if (dateFilter === '12months') {
        sinceDate = new Date();
        sinceDate.setMonth(sinceDate.getMonth() - 12);
      }
      // For "All Time", don't set sinceDate - we'll use force=true to bypass date filters
      
      // Determine which date field to use based on operator intent:
      // - Skip synced ON: prioritize records recently updated from 4D (external_updated_at)
      // - Skip synced OFF: prioritize appointment schedule date (start_time) for historical backlog sweeps
      const useStartTime = !skipSyncedOption;
      const dateField = useStartTime ? 'start_time' : 'external_updated_at';
      
      // For "All Time", use force=true to bypass date filters on the server completely
      // This avoids timeout issues with very old date ranges
      const sinceParam = isAllTime 
        ? '&force=true' // Bypass date filters entirely for All Time
        : `&since=${sinceDate!.toISOString()}${useStartTime ? '&use_start_time=true' : ''}`;
      
      // Always let backend use actionable-status defaults for appointment pushes.
      // Including terminal statuses (skipped/no_patient/not_mapped) causes repeat loops in batched sync.
      const statusParams = '';
      const orderParams = '&order=asc'; // Process oldest first
      // Larger batches: appointments use multiple GHL calls each, but 26s timeout + 100/10s burst
      // supports ~20-30 appointments per invocation better than the old size of 5.
      const targetBatchSize = 25;
      let batchSize = targetBatchSize;
      let totalPushed = 0;
      // Absolute safety only — All Time Ottawa-scale backlogs need far more than 1000 batches.
      // Stop conditions below are batchTotal===0 (drained) or empty consecutive empty fetches.
      const maxIterations = isAllTime ? 20000 : 2000;
      let hitIterationCap = false;

      // Get total count of appointments to sync (for progress tracking)
      // For "All Time", skip the count query to avoid timeouts - we'll show progress without total
      let expectedTotal = 0;
      if (!isAllTime) {
        try {
          let countQuery = supabase
            .from('appointments')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', userId)
            // Don't filter out null patient_id - sync function processes these to mark as "no_patient"
            .gte(dateField, sinceDate!.toISOString()); // Use appropriate date field based on status filter

          // Count only actionable statuses to match push-appointments default behavior.
          // Excludes terminal statuses (skipped/no_patient/not_mapped) that should not be replayed in batches.
          countQuery = countQuery.or('zonedm_sync_status.eq.received,zonedm_sync_status.eq.zonedm_error,zonedm_sync_status.eq.waiting_for_patient,zonedm_sync_status.is.null');

          const { count } = await countQuery;
          expectedTotal = count || 0;

          if (expectedTotal > 0) {
            toast.loading(`Preparing to sync ${expectedTotal} appointments...`, { id: toastId });
          }
        } catch (countErr) {
          console.warn('Failed to get total count:', countErr);
          // Continue without total - not critical
        }
      } else {
        // For All Time, show a message that we're processing all historical data
        toast.loading('Syncing all historical appointments (this may take a while)...', { id: toastId });
      }

      // Helper function to process batches with optional waiting_for_patient filtering
      const processBatches = async (excludeWaiting: boolean, onlyWaiting: boolean, phaseName: string) => {
        let iteration = 0;
        let phasePushed = 0;
        let currentBatchSize = batchSize; // Use the current batch size (may have been reduced)
        let consecutiveZeroSuccess = 0;
        let lastFailFingerprint = '';
        
        const waitingParam = excludeWaiting ? '&exclude_waiting_for_patient=true' : (onlyWaiting ? '&only_waiting_for_patient=true' : '');
        const isRetryableStatus = (status: number) => status === 502 || status === 503 || status === 504;
        
        while (iteration < maxIterations) {
          iteration++;
          const pushApptsUrl = `/.netlify/functions/push-appointments?user_id=${encodeURIComponent(userId)}&limit=${currentBatchSize}${statusParams}${orderParams}${sinceParam}${waitingParam}`;
          
          let res2: Response | null = null;
          let body2: any = {};
          let retryCount = 0;
          const maxRetries = 3;
          
          // Retry logic for gateway / overload / timeout errors
          while (retryCount < maxRetries) {
            try {
              res2 = await fetch(pushApptsUrl, {
                method: 'POST',
                headers: { Authorization: `Bearer ${session.access_token}` },
              });
              body2 = await res2.json().catch(() => ({}));
              
              if (isRetryableStatus(res2.status) && retryCount < maxRetries - 1) {
                retryCount++;
                const backoffMs = Math.min(1000 * Math.pow(2, retryCount - 1), 5000);
                toast.loading(`Temporary error (${res2.status}), retrying... (${retryCount}/${maxRetries})`, { id: toastId });
                await new Promise(resolve => setTimeout(resolve, backoffMs));
                continue;
              }
              
              // If not a retryable error, break out of retry loop
              break;
            } catch (fetchErr: any) {
              // Network errors - retry with backoff
              if (retryCount < maxRetries - 1) {
                retryCount++;
                const backoffMs = Math.min(1000 * Math.pow(2, retryCount - 1), 5000);
                toast.loading(`Network error, retrying... (${retryCount}/${maxRetries})`, { id: toastId });
                await new Promise(resolve => setTimeout(resolve, backoffMs));
                continue;
              }
              // Last retry failed, throw error
              throw new Error(`Failed to push appointments after ${maxRetries} retries: ${fetchErr?.message || 'Unknown error'}`);
            }
          }
          
          // If res2 is still null after retries, something went wrong
          if (!res2) {
            throw new Error('Failed to fetch appointments after retries');
          }
          
          if (!res2.ok) {
            // After retries, shrink batch on gateway/timeout and keep going (do not treat as done)
            if (isRetryableStatus(res2.status)) {
              if (currentBatchSize > 1) {
                currentBatchSize = Math.max(1, Math.floor(currentBatchSize / 2));
                batchSize = currentBatchSize;
                toast.loading(`Temporary error (${res2.status}), reducing batch size to ${currentBatchSize}...`, { id: toastId });
              } else {
                toast.loading(`Temporary error (${res2.status}) at minimum batch size, retrying...`, { id: toastId });
              }
              await new Promise(resolve => setTimeout(resolve, 1000));
              continue;
            }
            throw new Error(body2?.error || `Failed pushing appointments: ${res2.status}`);
          }
          
          const batchSuccess = Number(body2?.success || 0);
          const batchTotal = Number(body2?.total || 0);
          const batchErrors = Number(body2?.errors || 0);
          
          // Log response for debugging
          console.log(`[Sync TO ZoneDM] ${phaseName} Response: success=${batchSuccess}, total=${batchTotal}, errors=${batchErrors}`, body2);
          
          phasePushed += batchSuccess;
          totalPushed += batchSuccess;
          
          // Log debug info if available (when total is 0)
          if (batchTotal === 0 && body2?.debug) {
            console.log(`[Sync TO ZoneDM] ${phaseName} Debug info:`, body2.debug);
            console.log(`[Sync TO ZoneDM] ${phaseName} Query params used: since=${sinceParam}, order=${orderParams}, status=${statusParams}, waitingParam=${waitingParam}`);
          }
          
          // Invalidate queries to update UI in real-time (like PatientsPage does)
          queryClient.invalidateQueries({ queryKey: ['appointments-table'] });
          queryClient.invalidateQueries({ queryKey: ['appointments-synced-count'] });
          
          // Only stop when the backend finds nothing left for this phase.
          // Do NOT stop on partial batches — push-appointments often returns early on its
          // 18s budget with total still equal to limit, but when total < limit it can also
          // mean a transient empty window; require a true empty result.
          if (batchTotal === 0 && batchErrors === 0) {
            if (body2?.debug) {
              console.warn(`[Sync TO ZoneDM] ${phaseName} No appointments found. Debug:`, body2.debug);
            }
            // Queue for this phase is drained
            break;
          }
          
          // If we processed appointments but all failed, log it but continue
          if (batchTotal > 0 && batchSuccess === 0 && batchErrors > 0) {
            console.warn(`[Sync TO ZoneDM] ${phaseName} Batch processed ${batchTotal} appointments but all failed (${batchErrors} errors)`);
            // Log the actual errors to help debug
            if (body2?.itemResults && Array.isArray(body2.itemResults)) {
              console.error(`[Sync TO ZoneDM] ${phaseName} Error details:`, body2.itemResults.map((r: any) => ({
                id: r.id,
                external_id: r.external_id,
                status: r.status,
                error: r.error,
                steps: r.steps
              })));
            }
            // Same hard-fail rows looping (e.g. one zonedm_error forever) — stop this phase
            const fingerprint = Array.isArray(body2?.itemResults)
              ? body2.itemResults.map((r: any) => String(r.id || r.external_id || '')).sort().join('|')
              : `n=${batchTotal}`;
            if (fingerprint && fingerprint === lastFailFingerprint) {
              consecutiveZeroSuccess++;
            } else {
              consecutiveZeroSuccess = 1;
              lastFailFingerprint = fingerprint;
            }
            if (consecutiveZeroSuccess >= 3) {
              console.warn(`[Sync TO ZoneDM] ${phaseName} stopping after ${consecutiveZeroSuccess} identical all-fail batches`);
              toast.loading(
                `Syncing appointments (${phaseName})... paused after repeated failures on the same records; continuing…`,
                { id: toastId }
              );
              break;
            }
          } else {
            consecutiveZeroSuccess = 0;
            lastFailFingerprint = '';
          }

          // After a healthy full batch, climb batch size back toward target (timeouts shrink it)
          if (batchTotal >= currentBatchSize && currentBatchSize < targetBatchSize && batchErrors === 0) {
            currentBatchSize = Math.min(targetBatchSize, currentBatchSize + 5);
            batchSize = currentBatchSize;
          }
          
          const progressMsg = expectedTotal > 0
            ? `Syncing appointments (${phaseName})... ${totalPushed}/${expectedTotal} synced (${batchSuccess}/${batchTotal} in this batch)`
            : `Syncing appointments (${phaseName})... ${totalPushed} synced (${batchSuccess}/${batchTotal} in this batch)`;
          toast.loading(progressMsg, { id: toastId });
          
          // Small delay to avoid overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        if (iteration >= maxIterations) {
          hitIterationCap = true;
          console.warn(`[Sync TO ZoneDM] ${phaseName} hit iteration cap (${maxIterations}); click Sync again to continue`);
        }
        
        return phasePushed;
      };

      // PHASE 1: Process all records EXCEPT waiting_for_patient (to avoid re-processing blocked records)
      console.log('[Sync TO ZoneDM] Starting Phase 1: Processing non-wait-blocked records...');
      const phase1Pushed = await processBatches(true, false, 'Phase 1 (non-wait-blocked)');
      console.log(`[Sync TO ZoneDM] Phase 1 complete: ${phase1Pushed} appointments synced`);
      
      // PHASE 2: Process only waiting_for_patient records (these were blocked earlier, now retry them)
      console.log('[Sync TO ZoneDM] Starting Phase 2: Processing wait-blocked records...');
      const phase2Pushed = await processBatches(false, true, 'Phase 2 (wait-blocked)');
      console.log(`[Sync TO ZoneDM] Phase 2 complete: ${phase2Pushed} appointments synced`);

      if (hitIterationCap) {
        toast.success(
          `Sync paused after a long run (${totalPushed} appointments this session). Click Sync TO ZoneDM again to continue the backlog.`,
          { id: toastId, duration: 8000 }
        );
      } else {
        const finalMsg = expectedTotal > 0
          ? `Sync TO ZoneDM completed: ${totalPushed}/${expectedTotal} appointments synced`
          : `Sync TO ZoneDM completed: ${totalPushed} appointments synced`;
        toast.success(finalMsg, { id: toastId });
      }
      // Refresh table to reflect updated zonedm_sync_status
      queryClient.invalidateQueries({ queryKey: ['appointments-table'] });
      queryClient.invalidateQueries({ queryKey: ['appointments-synced-count'] });
    } catch (err: any) {
      toast.error('Sync TO ZoneDM failed: ' + (err?.message || 'Unknown error'), { id: toastId });
    } finally {
      setSyncingToZoneDM(false);
    }
  };

  const asDisplay = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const formatDateTime = (value?: string | null): string => {
    if (!value) return '—';
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
  };

  const fullName = (...parts: Array<unknown>): string =>
    parts
      .map((part) => (part == null ? '' : String(part).trim()))
      .filter(Boolean)
      .join(' ')
      .trim();

  const appointmentDetailSections = useMemo(() => {
    if (!selectedAppointment) return [];

    const appt = selectedAppointment;
    const patientName = fullName(appt?.patients?.first_name, appt?.patients?.last_name);
    const providerName = fullName(appt?.providers?.first_name, appt?.providers?.last_name);
    const staffName = fullName(appt?.staff?.first_name, appt?.staff?.last_name);
    const providerDisplay = providerName
      ? `${providerName}${appt?.providers?.degree ? `, ${appt.providers.degree}` : ''}`
      : staffName;
    const procedureList = proceduresData?.[appt.id];
    const procedureDisplay = procedureList?.length
      ? procedureList.join(', ')
      : asDisplay(appt?.procedure_description);
    const typeId = appt.type_id ?? appt.appointment_type_id;
    const subtypeId = appt.subtype_id ?? appt.appointment_subtype_id;
    const syncStatus = asDisplay(appt.zonedm_sync_status ?? appt.sync_status ?? 'received');

    const sections = [
      {
        title: 'Identity',
        rows: [
          {
            label: 'Patient',
            primary: patientName || '—',
            secondary: appt.patient_id ? `patient_id: ${appt.patient_id}` : undefined,
          },
          {
            label: 'Provider / Staff',
            primary: providerDisplay || '—',
            secondary: [
              appt.provider_id ? `provider_id: ${appt.provider_id}` : null,
              appt.staff_id ? `staff_id: ${appt.staff_id}` : null,
            ]
              .filter(Boolean)
              .join(' • ') || undefined,
          },
          {
            label: 'Appointment Type',
            primary: asDisplay(appt?.appointment_types?.name),
            secondary: typeId ? `type_id: ${typeId}` : undefined,
          },
          {
            label: 'Appointment Subtype',
            primary: asDisplay(appt?.appointment_subtypes?.name),
            secondary: subtypeId ? `subtype_id: ${subtypeId}` : undefined,
          },
          {
            label: 'Procedure',
            primary: procedureDisplay,
            secondary: [
              appt.quote_number != null ? `quote_number: ${appt.quote_number}` : null,
              appt.quote_external_id != null ? `quote_external_id: ${appt.quote_external_id}` : null,
            ]
              .filter(Boolean)
              .join(' • ') || undefined,
          },
        ],
      },
      {
        title: '4D Mapping',
        rows: [
          {
            label: '4D Appointment ID',
            primary: asDisplay(appt.external_id),
          },
          {
            label: 'Facility',
            primary: facilityNameByExternalId(appt.facility_location_id),
            secondary: appt.facility_location_id != null
              ? `facility_location_id: ${appt.facility_location_id}`
              : undefined,
          },
          {
            label: 'Received From 4D',
            primary: formatDateTime(appt.external_updated_at),
            secondary: appt.external_updated_at ? `external_updated_at: ${appt.external_updated_at}` : undefined,
          },
        ],
      },
      {
        title: 'ZoneDM Mapping',
        rows: [
          {
            label: 'ZoneDM Sync Status',
            primary: syncStatus,
            secondary: appt.zonedm_sync_error ? `error: ${appt.zonedm_sync_error}` : undefined,
          },
          {
            label: 'ZoneDM Contact ID',
            primary: asDisplay(appt.zonedm_contact_id),
          },
          {
            label: 'ZoneDM Opportunity ID',
            primary: asDisplay(appt.zonedm_opportunity_id),
          },
          {
            label: 'ZoneDM Event ID',
            primary: asDisplay(appt.zonedm_event_id),
          },
        ],
      },
      {
        title: 'Internal DB',
        rows: [
          {
            label: 'Appointment UUID',
            primary: asDisplay(appt.id),
          },
          {
            label: 'User UUID',
            primary: asDisplay(appt.user_id),
          },
          {
            label: 'Created At',
            primary: formatDateTime(appt.created_at),
            secondary: appt.created_at ? `created_at: ${appt.created_at}` : undefined,
          },
          {
            label: 'Updated At',
            primary: formatDateTime(appt.updated_at),
            secondary: appt.updated_at ? `updated_at: ${appt.updated_at}` : undefined,
          },
        ],
      },
      {
        title: 'Record Context',
        rows: [
          {
            label: 'Start Time',
            primary: formatDateTime(appt.start_time),
            secondary: appt.start_time ? `start_time: ${appt.start_time}` : undefined,
          },
          {
            label: 'End Time',
            primary: formatDateTime(appt.end_time),
            secondary: appt.end_time ? `end_time: ${appt.end_time}` : undefined,
          },
          {
            label: 'Status',
            primary: asDisplay(appt.status),
          },
          {
            label: 'Subject',
            primary: asDisplay(appt.subject),
          },
          {
            label: 'Consult Reason',
            primary: asDisplay(appt.consult_reason),
          },
          {
            label: 'Cancel Reason',
            primary: asDisplay(appt.cancel_reason),
          },
        ],
      },
    ];

    return sections
      .map((section) => ({
        ...section,
        rows: section.rows.filter((row) => row.primary !== '—' || Boolean(row.secondary)),
      }))
      .filter((section) => section.rows.length > 0);
  }, [selectedAppointment, proceduresData, facilities]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 lg:sticky lg:top-0 lg:z-40 lg:bg-white lg:py-3 lg:mb-4 lg:border-b lg:border-gray-200">
        <h1 className="text-2xl font-bold">Appointments</h1>
        <div className="flex-1 flex justify-center">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700 font-medium">Search:</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => { setSearchInput(e.target.value); }}
              placeholder="Patient name, status, cancel reason, consult reason, quote"
              className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white min-w-[260px]"
            />
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div>Synced: {appointmentsSyncedCount.toLocaleString()}/{appointmentsTotalCount.toLocaleString()}</div>
          <button onClick={() => setConfirmSyncOpen(true)} className="bg-blue-600 text-white px-4 py-2 rounded flex items-center justify-center min-w-[120px]" disabled={syncingFrom4D}>
            {syncingFrom4D ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                Syncing...
              </>
            ) : (
              'Sync FROM 4D'
            )}
          </button>
          <button onClick={() => setConfirmZoneDMOpen(true)} className="bg-indigo-600 text-white px-4 py-2 rounded flex items-center justify-center min-w-[140px]" disabled={syncingToZoneDM}>
            {syncingToZoneDM ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                Sync TO ZoneDM
              </>
            ) : (
              'Sync TO ZoneDM'
            )}
          </button>
        </div>
      </div>

      <Card>
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-gray-50 border border-gray-200 rounded-md px-4 py-3 mb-4 lg:sticky lg:top-[86px] lg:z-30">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 font-medium">Rows per page:</label>
              <select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={500}>500</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 font-medium">Facility:</label>
              <select
                value={facilityFilter === 'all' ? 'all' : String(facilityFilter)}
                onChange={(e) => {
                  const v = e.target.value;
                  setFacilityFilter(v === 'all' ? 'all' : Number(v));
                  setPage(1);
                }}
                className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white"
              >
                <option value="all">All Locations</option>
                {(facilities || []).map((f: any) => (
                  <option key={f.id} value={f.external_id}>{f.facility_location_name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 font-medium">Date Range:</label>
              <select
                value={dateFilter}
                onChange={(e) => { setDateFilter(e.target.value as '3months' | '6months' | '12months' | 'all'); setPage(1); }}
                className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white"
              >
                <option value="3months">Last 3 Months</option>
                <option value="6months">Last 6 Months</option>
                <option value="12months">Last 12 Months</option>
                <option value="all">All Time</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 font-medium">Sort:</label>
              <select
                value={sortOrder}
                onChange={(e) => { setSortOrder(e.target.value as 'newest' | 'oldest'); setPage(1); }}
                className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 font-medium">ZoneDM Status:</label>
              <select
                value={syncStatusFilter}
                onChange={(e) => { setSyncStatusFilter(e.target.value); setPage(1); }}
                className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white"
              >
                <option value="all">All Statuses</option>
                <option value="received">Received</option>
                <option value="zonedm_synced">Synced</option>
                <option value="zonedm_error">Error</option>
                <option value="waiting_for_patient">Waiting for Patient</option>
                <option value="skipped">Skipped</option>
                <option value="skipped_archived">Skipped (Archived)</option>
                <option value="not_mapped">Not Mapped</option>
                <option value="no_patient">No Patient</option>
                <option value="missing_contact">Missing Contact</option>
                <option value="empty">Empty</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="has-quote"
                type="checkbox"
                checked={hasQuoteOnly}
                onChange={(e) => { setHasQuoteOnly(e.target.checked); setPage(1); }}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
              />
              <label htmlFor="has-quote" className="text-sm text-gray-700 font-medium">Has Quote Only</label>
            </div>
            <div className="text-sm text-gray-500 font-medium">
              Page {page} of {totalPages} ({appointmentsCount.toLocaleString()} total)
            </div>
          </div>

          <div className="relative">
            {/* Top scrollbar - synchronized with table scroll */}
            <div 
              ref={topScrollbarRef}
              className="overflow-x-auto mb-0 lg:sticky lg:top-[168px] lg:z-20"
              style={{ 
                height: '17px',
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb'
              }}
              onScroll={(e) => {
                if (tableContainerRef.current) {
                  tableContainerRef.current.scrollLeft = e.currentTarget.scrollLeft;
                }
              }}
            >
              <div style={{ height: '1px', width: `${topScrollbarWidth}px` }}></div>
            </div>
            {/* Table container with synchronized scroll */}
            <div 
              ref={tableContainerRef}
              className="overflow-x-auto"
              onScroll={(e) => {
                if (topScrollbarRef.current) {
                  topScrollbarRef.current.scrollLeft = e.currentTarget.scrollLeft;
                }
              }}
            >
              <table className="min-w-full divide-y divide-gray-200 text-xs shadow-sm border border-gray-200 rounded-md bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Received From 4D</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Consult Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtype</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Procedure</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ZoneDM Sync</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cancel Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facility</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quote #</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(!paginatedAppointments || paginatedAppointments.length === 0) && (
                <tr>
                  <td colSpan={14} className="px-6 py-8 text-center text-gray-400">{isLoading ? 'Loading...' : 'No appointments found.'}</td>
                </tr>
              )}
              {paginatedAppointments.map((a: any) => (
                <tr
                  key={a.id}
                  className="border-t hover:bg-gray-50 cursor-pointer"
                  onClick={() => { setSelectedAppointment(a); setIsModalOpen(true); }}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-700 underline">
                    {a.patient_id ? (
                      <Link 
                        to={`/dashboard/patients?search=${encodeURIComponent(String(a.patient_id))}`}
                        title={a.patient_id}
                        className="hover:text-indigo-900"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {a?.patients?.first_name || a?.patients?.last_name 
                          ? `${a.patients.first_name || ''} ${a.patients.last_name || ''}`.trim()
                          : a.patient_id
                        }
                      </Link>
                    ) : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{a.start_time ? new Date(a.start_time).toLocaleString() : '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{a.end_time ? new Date(a.end_time).toLocaleString() : '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" title={`Received from 4D: ${a.external_updated_at ? new Date(a.external_updated_at).toLocaleString() : 'Never'}`}>
                    {a.external_updated_at ? new Date(a.external_updated_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{a.consult_reason || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{a?.appointment_types?.name || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{a?.appointment_subtypes?.name || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(() => {
                      // Check providers first (if they have actual data)
                      if (a?.providers && (a.providers.first_name || a.providers.last_name)) {
                        const name = `${a.providers.first_name || ''} ${a.providers.last_name || ''}`.trim();
                        return name ? `${name}${a.providers.degree ? `, ${a.providers.degree}` : ''}` : '—';
                      }
                      // Fallback to staff if providers not available
                      if (a?.staff && (a.staff.first_name || a.staff.last_name)) {
                        return `${a.staff.first_name || ''} ${a.staff.last_name || ''}`.trim() || '—';
                      }
                      return '—';
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(() => {
                      // Get procedure names from proceduresData map
                      const procedures = proceduresData?.[a.id];
                      if (procedures && procedures.length > 0) {
                        return procedures.join(', ');
                      }
                      return '—';
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{a.status || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {(() => {
                      const status = (a.zonedm_sync_status ?? a.sync_status) as string | undefined;
                      const errorMsg = (a.zonedm_sync_error as string | undefined) || undefined;
                      const base = 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full';
                      const cls = !status || status === 'received'
                        ? 'bg-yellow-100 text-yellow-800'
                        : status === 'zonedm_synced' || status === 'synced'
                        ? 'bg-green-100 text-green-800'
                        : status === 'zonedm_error'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800';
                      const pill = (
                        <span className={`${base} ${cls}`}>{status || 'received'}</span>
                      );
                      if (status === 'zonedm_error' && errorMsg) {
                        return (
                          <button
                            type="button"
                            className="underline decoration-dotted decoration-red-400"
                            onClick={(e) => { e.stopPropagation(); toast.error(errorMsg); }}
                            title={errorMsg}
                          >
                            {pill}
                          </button>
                        );
                      }
                      return pill;
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{a.cancel_reason || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{facilityNameByExternalId(a.facility_location_id)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-700 underline">
                    {a.quote_number ? (
                      <Link to={`/dashboard/quotes?search=${encodeURIComponent(String(a.quote_number))}`}>
                        {a.quote_number}
                      </Link>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-6">
            <button
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              Previous
            </button>
            <div className="flex flex-wrap gap-1 justify-center">
              {(() => {
                // Show max 10 page buttons at a time, centered around current page
                const maxButtons = 10;
                let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
                let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                
                // Adjust if we're near the end
                if (endPage - startPage < maxButtons - 1) {
                  startPage = Math.max(1, endPage - maxButtons + 1);
                }
                
                const pages: (number | string)[] = [];
                
                // Add first page if not in range
                if (startPage > 1) {
                  pages.push(1);
                  if (startPage > 2) pages.push('...');
                }
                
                // Add page numbers
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(i);
                }
                
                // Add last page if not in range
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) pages.push('...');
                  pages.push(totalPages);
                }
                
                return pages.map((pNum, idx) => 
                  pNum === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 py-1 text-gray-500">...</span>
                  ) : (
                    <button
                      key={pNum}
                      className={`px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none ${pNum === page ? 'bg-blue-100 font-bold border-blue-400' : ''}`}
                      onClick={() => handlePageChange(pNum as number)}
                      disabled={pNum === page}
                    >
                      {pNum}
                    </button>
                  )
                );
              })()}
            </div>
            <button
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </Card>

      {/* Appointment Details Modal */}
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" onClick={() => setIsModalOpen(false)} />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-3xl w-full bg-white rounded-xl shadow-lg p-6 relative">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold mb-4">Appointment Details</h2>
            {selectedAppointment && (
              <div className="max-h-[70vh] overflow-y-auto pr-2">
                {appointmentDetailSections.map((section) => (
                  <div key={section.title} className="mb-6">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">{section.title}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                      {section.rows.map((row) => (
                        <React.Fragment key={`${section.title}-${row.label}`}>
                          <div className="text-right font-semibold text-gray-700 truncate pr-2 py-1 border-b border-gray-100">
                            {row.label}
                          </div>
                          <div className="text-left text-gray-900 break-all py-1 border-b border-gray-100">
                            <div>{row.primary}</div>
                            {row.secondary && (
                              <div className="text-xs text-gray-500 font-mono mt-1">{row.secondary}</div>
                            )}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Confirm Sync FROM 4D Modal */}
      <Dialog open={confirmSyncOpen} onClose={() => setConfirmSyncOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" onClick={() => setConfirmSyncOpen(false)} />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-xl shadow-lg p-6 relative">
            <button
              onClick={() => setConfirmSyncOpen(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
            <Dialog.Title className="text-lg font-semibold text-gray-900">Confirm Sync FROM 4D</Dialog.Title>
            <div className="mt-3 text-sm text-gray-600">
              This will fetch appointments from 4D EMR and sync them to Supabase. The sync will paginate through all available appointments (100 per page).
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                checked={skipSynced}
                onChange={(e) => setSkipSynced(e.target.checked)}
              />
              <span>Skip synced appointments (start from next page after existing sync)</span>
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                onClick={() => setConfirmSyncOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                onClick={async () => {
                  setConfirmSyncOpen(false);
                  await syncFrom4D(skipSynced);
                }}
                disabled={syncingFrom4D}
              >
                Confirm
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Confirm Sync TO ZoneDM Modal */}
      <Dialog open={confirmZoneDMOpen} onClose={() => setConfirmZoneDMOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" onClick={() => setConfirmZoneDMOpen(false)} />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-xl shadow-lg p-6 relative">
            <button
              onClick={() => setConfirmZoneDMOpen(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
            <Dialog.Title className="text-lg font-semibold text-gray-900">Confirm Sync TO ZoneDM</Dialog.Title>
            <div className="mt-3 text-sm text-gray-600">
              This will push patients and appointments to ZoneDM (HighLevel). Patients will be synced first to ensure contacts exist, then appointments will be created as calendar events.
            </div>
            <label className="mt-4 flex items-start gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                checked={skipSyncedZoneDM}
                onChange={(e) => setSkipSyncedZoneDM(e.target.checked)}
              />
              <span>Skip already synced items (uncheck for historical backlog sweep of actionable records; terminal statuses are excluded)</span>
            </label>
            <label className="mt-3 flex items-start gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                checked={premarkUnmapped}
                onChange={(e) => setPremarkUnmapped(e.target.checked)}
              />
              <span>
                Premark unmapped/skippable first
                <span className="block text-xs text-gray-500 mt-0.5">
                  Speeds onboarding. Finish calendar mapping before enabling.
                </span>
              </span>
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                onClick={() => setConfirmZoneDMOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={async () => {
                  setConfirmZoneDMOpen(false);
                  await syncToZoneDM(skipSyncedZoneDM, premarkUnmapped);
                }}
                disabled={syncingToZoneDM}
              >
                Confirm
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}


